import { logger } from '@kuow/core';
import type { CardData } from './types';

export type AuthorizeNetEnvironment = 'production' | 'test';

export interface AuthorizeNetConfig {
  environment: AuthorizeNetEnvironment;
}

/**
 * Map our environment names to Authorize.Net's environment config
 * - production → production
 * - staging → test
 * - development → test
 * - anything else → test
 */
export function getAuthorizeNetEnvironment(environment: string): AuthorizeNetEnvironment {
  return environment === 'production' ? 'production' : 'test';
}

export interface AuthorizeNetResponse {
  encryptedCardData: {
    cardNumber: string;
    expDate: string;
    bin: string;
  };
  opaqueData: {
    dataValue: string;
    dataDescriptor: string;
  };
  messages: {
    resultCode: string;
    message: { text: string }[];
  };
}

const AUTHORIZE_NET_URLS = {
  production: 'https://js.authorize.net/v3/AcceptUI.js',
  test: 'https://jstest.authorize.net/v3/AcceptUI.js',
};

const AUTHORIZE_NET_APP_IDS = {
  production: '67D2mbR3',
  test: '49e4ybQYXrwG',
};

const AUTHORIZE_NET_CLIENT_KEYS = {
  production: '6nQk5uU6q34UfvB5c82F572ESkmNFc6KNv62pa8LZuSV2SsSGtTaDs8jS5B7sdT7',
  test: '7758TCDBBvU24b2CRacgqd6pV64A4yfSNyMekCzaHDGtcfEB5uuS6HSnE4GGS2YU',
};

const AUTHORIZE_NET_Z_INDEX = 2147483647;
const ENABLE_AUTHORIZE_NET_DIALOG_HIDE_HACK = false;
let buttonRef: HTMLButtonElement | null = null;
let scriptLoaded = false;
let modalObserver: MutationObserver | null = null;
let modalDismissCallback: (() => void) | null = null;
let modalPollingInterval: number | null = null;

// Store dialog elements and their original display/pointer-events values for restoration
interface HiddenElement {
  element: HTMLElement;
  originalDisplay: string;
  originalPointerEvents: string;
}
let hiddenBackdrops: HiddenElement[] = [];
let hiddenPositioners: HiddenElement[] = [];
let bodyOriginalPointerEvents: string | null = null;

/**
 * Get the Authorize.Net script URL based on environment
 */
function getScriptUrl(config: AuthorizeNetConfig): string {
  return AUTHORIZE_NET_URLS[config.environment];
}

/**
 * Get the Authorize.Net App ID based on environment
 */
function getAppId(config: AuthorizeNetConfig): string {
  return AUTHORIZE_NET_APP_IDS[config.environment];
}

/**
 * Get the Authorize.Net Client Key based on environment
 */
function getClientKey(config: AuthorizeNetConfig): string {
  return AUTHORIZE_NET_CLIENT_KEYS[config.environment];
}

/**
 * Clean up redundant Authorize.Net DOM elements and scripts.
 * If fullReset is true, removes all elements and scripts to allow fresh initialization.
 */
function cleanupAuthorizeNetElements(config: AuthorizeNetConfig, fullReset = false): void {
  const containers = document.querySelectorAll('#AcceptUIContainer');
  const scriptUrl = getScriptUrl(config);
  const scriptTags = document.querySelectorAll(`script[src="${scriptUrl}"]`);
  const buttons = document.querySelectorAll('.AcceptUI');

  if (fullReset) {
    // Full reset: remove everything so script can reinitialize fresh
    containers.forEach((container) => {
      container.parentNode?.removeChild(container);
    });
    scriptTags.forEach((script) => {
      script.parentNode?.removeChild(script);
    });
    buttons.forEach((button) => {
      button.parentNode?.removeChild(button);
    });
    scriptLoaded = false;
    buttonRef = null;
  } else {
    // Partial cleanup: keep most recent container and script
    if (containers.length > 1) {
      for (let i = 0; i < containers.length - 1; i++) {
        containers[i].parentNode?.removeChild(containers[i]);
      }
    }
    if (scriptTags.length > 1) {
      for (let i = 0; i < scriptTags.length - 1; i++) {
        scriptTags[i].parentNode?.removeChild(scriptTags[i]);
      }
    }
    buttons.forEach((button) => {
      button.parentNode?.removeChild(button);
    });
  }
}

/**
 * Load the Authorize.Net Accept.JS script
 */
function loadAuthorizeNetScript(config: AuthorizeNetConfig): Promise<void> {
  return new Promise((resolve, reject) => {
    const scriptUrl = getScriptUrl(config);
    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);

    if (existingScript) {
      scriptLoaded = true;
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => {
      scriptLoaded = true;
      resolve();
    };
    script.onerror = () => {
      reject(new Error('Failed to load Authorize.Net script'));
    };

    document.body.appendChild(script);
  });
}

/**
 * Parse the Authorize.Net response and extract card data
 */
function parseAuthorizeNetResponse(response: AuthorizeNetResponse): CardData {
  const { encryptedCardData, opaqueData } = response;
  const [expMonth, expYear] = encryptedCardData.expDate ? encryptedCardData.expDate.split('/') : ['', ''];

  return {
    cardNumber: encryptedCardData.cardNumber,
    expMonth,
    expYear,
    bin: encryptedCardData.bin,
    dataValue: opaqueData.dataValue,
    dataDescriptor: opaqueData.dataDescriptor,
  };
}

/**
 * Create and configure the hidden Authorize.Net button
 */
function createAuthorizeNetButton(config: AuthorizeNetConfig): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'AcceptUI';
  button.style.display = 'none';

  // cSpell:disable
  button.setAttribute('data-billingaddressoptions', '{"show": true, "required": false}');
  button.setAttribute('data-apiloginid', getAppId(config));
  button.setAttribute('data-clientkey', getClientKey(config));
  button.setAttribute('data-acceptuiformbtntxt', 'Submit');
  button.setAttribute('data-acceptuiformheadertxt', 'Payment Information');
  button.setAttribute('data-paymentoptions', '{"showCreditCard": true, "showBankAccount": false}');
  button.setAttribute('data-responsehandler', 'authNetResponseHandler');
  // cSpell:enable

  return button;
}

/**
 * Find dialog backdrop and positioner elements in both regular DOM and Shadow DOM
 */
function findDialogElements(): { backdrops: HTMLElement[]; positioners: HTMLElement[] } {
  const backdrops: HTMLElement[] = [];
  const positioners: HTMLElement[] = [];

  // Helper to search in a root (document or shadow root)
  const searchInRoot = (root: Document | ShadowRoot) => {
    // Find backdrops
    root.querySelectorAll('[data-scope="dialog"][data-part="backdrop"]').forEach((el) => {
      if (el instanceof HTMLElement) backdrops.push(el);
    });
    
    // Find positioners
    root.querySelectorAll('[data-scope="dialog"][data-part="positioner"]').forEach((el) => {
      if (el instanceof HTMLElement) positioners.push(el);
    });
  };

  // Search in main document
  searchInRoot(document);

  // Search in all Shadow DOMs
  const allElements = document.querySelectorAll('*');
  allElements.forEach((element) => {
    if (element.shadowRoot) {
      searchInRoot(element.shadowRoot);
    }
  });

  return { backdrops, positioners };
}

/**
 * Hide dialog backdrop and positioner to allow interaction with Authorize.Net modal
 */
function hideDialogElements(): void {
  if (!ENABLE_AUTHORIZE_NET_DIALOG_HIDE_HACK) return;
  const { backdrops, positioners } = findDialogElements();
  
  // Store and hide backdrops
  backdrops.forEach((backdrop) => {
    hiddenBackdrops.push({
      element: backdrop,
      originalDisplay: backdrop.style.display || '',
      originalPointerEvents: backdrop.style.pointerEvents || '',
    });
    backdrop.style.display = 'none';
    backdrop.style.pointerEvents = 'none';
  });

  // Store and hide positioners
  positioners.forEach((positioner) => {
    hiddenPositioners.push({
      element: positioner,
      originalDisplay: positioner.style.display || '',
      originalPointerEvents: positioner.style.pointerEvents || '',
    });
    positioner.style.display = 'none';
    positioner.style.pointerEvents = 'none';
  });

  // Re-enable pointer events on body to allow interaction with Authorize.Net
  if (document.body.style.pointerEvents === 'none') {
    bodyOriginalPointerEvents = 'none';
    document.body.style.pointerEvents = 'auto';
    logger.info('Enabled body pointer events for Authorize.Net');
  }

  if (backdrops.length > 0 || positioners.length > 0) {
    logger.info('Hidden dialog elements for Authorize.Net', { 
      backdrops: backdrops.length, 
      positioners: positioners.length 
    });
  }

}

/**
 * Restore hidden dialog elements
 */
function restoreDialogElements(): void {
  if (!ENABLE_AUTHORIZE_NET_DIALOG_HIDE_HACK) return;
  // Add a small delay to ensure Authorize.Net modal is fully closed
  setTimeout(() => {
    // Restore backdrops
    hiddenBackdrops.forEach(({ element, originalDisplay, originalPointerEvents }) => {
      element.style.display = originalDisplay;
      element.style.pointerEvents = originalPointerEvents;
    });

    // Restore positioners
    hiddenPositioners.forEach(({ element, originalDisplay, originalPointerEvents }) => {
      element.style.display = originalDisplay;
      element.style.pointerEvents = originalPointerEvents;
    });

    // Restore body pointer events if we changed them
    if (bodyOriginalPointerEvents !== null) {
      document.body.style.pointerEvents = bodyOriginalPointerEvents;
      bodyOriginalPointerEvents = null;
      logger.info('Restored body pointer events');
    }

    if (hiddenBackdrops.length > 0 || hiddenPositioners.length > 0) {
      logger.info('Restored dialog elements', {
        backdrops: hiddenBackdrops.length,
        positioners: hiddenPositioners.length,
      });
    }

    hiddenBackdrops = [];
    hiddenPositioners = [];
  }, 100);
}

/**
 * Check if the modal is currently visible
 */
function isModalVisible(): boolean {
  const container = document.querySelector('#AcceptUIContainer');
  if (!container) return false;
  
  // Check for "show" class
  if (!container.classList.contains('show')) return false;
  
  return true;
}

function setAuthorizeNetZIndex(): void {
  const container = document.querySelector('#AcceptUIContainer');
  if (container instanceof HTMLElement) {
    container.style.zIndex = String(AUTHORIZE_NET_Z_INDEX);
  }
}

/**
 * Set up detection for when the AcceptUI modal is dismissed
 */
function setupModalDismissDetection(onDismiss: () => void): void {
  // Clean up any existing detection
  if (modalObserver) {
    modalObserver.disconnect();
    modalObserver = null;
  }
  if (modalPollingInterval) {
    clearInterval(modalPollingInterval);
    modalPollingInterval = null;
  }

  // Store the dismiss callback
  modalDismissCallback = onDismiss;

  let modalWasVisible = false;

  // Use polling as the primary detection method (more reliable than MutationObserver)
  const checkModalState = () => {
    const visible = isModalVisible();
    
    if (visible) {
      modalWasVisible = true;
      setAuthorizeNetZIndex();
    } else if (modalWasVisible && modalDismissCallback) {
      // Modal was visible but now it's not - it was dismissed
      logger.info('Authorize.Net modal dismissed (detected via polling)');
      
      // Restore dialog elements before calling callback
      restoreDialogElements();
      
      const callback = modalDismissCallback;
      modalDismissCallback = null;
      
      if (modalPollingInterval) {
        clearInterval(modalPollingInterval);
        modalPollingInterval = null;
      }
      
      if (modalObserver) {
        modalObserver.disconnect();
        modalObserver = null;
      }
      
      callback();
    }
  };

  // Poll every 250ms to check modal state
  modalPollingInterval = window.setInterval(checkModalState, 250);

  // Also set up MutationObserver as backup
  const setupObserver = () => {
    const container = document.querySelector('#AcceptUIContainer');
    if (container) {
      modalObserver = new MutationObserver(() => {
        checkModalState();
      });
      
      // Observe the modal container and its subtree for any changes
      modalObserver.observe(container, {
        attributes: true,
        attributeOldValue: true,
        childList: true,
        subtree: true,
      });
    }
  };

  // Try to set up observer immediately
  setupObserver();
  
  // Also try again after a delay in case container doesn't exist yet
  setTimeout(setupObserver, 500);
}

/**
 * Initialize Authorize.Net and open the payment modal
 */
export async function openAuthorizeNetModal(
  config: AuthorizeNetConfig,
  onSuccess: (cardData: CardData) => void,
  onError: (error: string) => void,
  onDismiss?: () => void,
): Promise<void> {
  try {
    // Full reset to ensure fresh initialization - AcceptUI.js binds handlers
    // on script load, so we must reload the script for new buttons to work
    cleanupAuthorizeNetElements(config, true);

    let responseHandled = false;

    // Set up the global response handler
    (window as unknown as { authNetResponseHandler?: (response: AuthorizeNetResponse) => void }).authNetResponseHandler = (response: AuthorizeNetResponse) => {
      responseHandled = true;
      
      // Restore dialog elements before processing response
      restoreDialogElements();
      
      // Clear the dismiss callback since we got a response
      modalDismissCallback = null;
      if (modalObserver) {
        modalObserver.disconnect();
        modalObserver = null;
      }

      if (response.messages.resultCode.toLowerCase() === 'ok') {
        const cardData = parseAuthorizeNetResponse(response);
        onSuccess(cardData);
      } else {
        const errorMessage = response.messages.message[0]?.text || 'Unknown error';
        logger.error('Authorize.Net error', { error: errorMessage });
        onError(errorMessage);
      }
    };

    // Set up modal dismiss detection if callback provided
    if (onDismiss) {
      setupModalDismissDetection(() => {
        if (!responseHandled) {
          logger.info('Authorize.Net modal dismissed without submission');
          onDismiss();
        }
      });
    }

    // Create and attach the button if it doesn't exist
    if (!buttonRef || !document.body.contains(buttonRef)) {
      buttonRef = createAuthorizeNetButton(config);
      document.body.appendChild(buttonRef);
    }

    // Load the script if not already loaded
    if (!scriptLoaded) {
      await loadAuthorizeNetScript(config);
    }

    // Wait a bit for the Accept.JS to initialize, then click the button
    setTimeout(() => {
      if (buttonRef) {
        buttonRef.click();
        
        // Hide dialog elements after modal opens to allow interaction
        setTimeout(() => {
          hideDialogElements();
        }, 300);
      }
    }, 700);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to initialize Authorize.Net';
    logger.error('Failed to open Authorize.Net modal', { error: errorMessage });
    restoreDialogElements(); // Restore on error
    onError(errorMessage);
  }
}

/**
 * Clean up Authorize.Net resources
 */
export function cleanupAuthorizeNet(): void {
  // Restore hidden dialog elements
  restoreDialogElements();
  
  // Remove the global response handler
  delete (window as unknown as { authNetResponseHandler?: unknown }).authNetResponseHandler;

  // Clean up modal observer
  if (modalObserver) {
    modalObserver.disconnect();
    modalObserver = null;
  }
  
  // Clean up polling interval
  if (modalPollingInterval) {
    clearInterval(modalPollingInterval);
    modalPollingInterval = null;
  }
  
  modalDismissCallback = null;

  // Note: We don't do a full reset here because openAuthorizeNetModal
  // will handle it on the next open. This avoids unnecessary script removal
  // when the component unmounts but the page remains.
}


